import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, Award, Beaker } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/products?sort_by=rating&order=desc`);
        setFeaturedProducts(data.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Lab-Grade Purity',
      description: '99%+ purity verified through third-party HPLC testing'
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: 'Fast Shipping',
      description: 'Orders processed within 24-48 hours of placement'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Free Shipping',
      description: 'Free standard shipping on all orders over $250'
    },
    {
      icon: <Beaker className="w-8 h-8" />,
      title: 'Research Grade',
      description: 'Formulated exclusively for scientific research purposes'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-[1]">
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-title"
            >
              ADVANCED<br />
              RESEARCH<br />
              <span className="text-gradient-cyan">PEPTIDES.</span>
            </h1>
            <p className="text-lg text-[#A1A7B5] mb-8 max-w-lg">
              Precision science for breakthrough discoveries. Lab-grade purity, unparalleled quality for your research needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button className="btn-primary flex items-center space-x-2 group" data-testid="explore-catalog-btn">
                  <span>Explore Catalog</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/about">
                <Button className="btn-secondary" data-testid="learn-more-btn">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 relative z-[1]" data-testid="video-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2
              className="text-2xl sm:text-3xl font-bold text-white mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              What Are <span className="text-[#00F0FF]">Peptides</span>?
            </h2>
            <p className="text-[#A1A7B5] text-sm max-w-xl mx-auto">
              Tiny molecules revolutionizing healing, anti-aging, weight loss, and personalized health.
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-[#1E2532] shadow-2xl shadow-[#00F0FF]/5">
            <video
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video bg-black"
              poster=""
              data-testid="hero-video"
            >
              <source src="https://customer-assets.emergentagent.com/job_peptide-hub-17/artifacts/wf2vdr5v_Peptides-Explained-Tiny-Molecules-Revolutionizing-Healing%2C-Anti-Aging%2C-Weight-Loss%2C-and-Personalized-Health.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#0C0E14]/80 backdrop-blur-sm relative z-[1]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Choose <span className="text-[#00F0FF]">RENEW LABS</span>
            </h2>
            <p className="text-[#A1A7B5] max-w-2xl mx-auto">
              We are committed to providing the highest quality research peptides with rigorous testing and quality assurance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="glass-card p-6 text-center"
                data-testid={`feature-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00F0FF]/10 rounded-full flex items-center justify-center text-[#00F0FF]">
                  {feature.icon}
                </div>
                <h3 className="text-white font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-[#A1A7B5] text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 relative z-[1]" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Top Rated Products
              </h2>
              <p className="text-[#A1A7B5]">
                Our most popular research peptides
              </p>
            </div>
            <Link to="/products">
              <Button className="btn-secondary flex items-center space-x-2" data-testid="view-all-btn">
                <span>View All</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="glass-card p-4">
                  <div className="skeleton h-48 w-full rounded-lg mb-4" />
                  <div className="skeleton h-4 w-3/4 mb-2" />
                  <div className="skeleton h-3 w-full mb-2" />
                  <div className="skeleton h-6 w-1/4" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Product Quality Guarantee */}
      <section className="py-20 bg-[#0C0E14]/80 backdrop-blur-sm relative z-[1]" data-testid="quality-guarantee-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#00F0FF] text-sm font-semibold uppercase tracking-[0.2em] mb-3">
              High Quality — 99% Purity Guaranteed
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Product Quality Guarantee
            </h2>
          </div>

          <div className="relative">
            {/* Animated glow border */}
            <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-[#00F0FF]/40 via-transparent to-[#00F0FF]/40 opacity-60 blur-sm" />
            <div className="relative bg-[#0A0C12] border border-[#1E2532] rounded-2xl p-8 sm:p-12">
              <p className="text-[#C8CDD8] text-base sm:text-lg leading-relaxed text-center max-w-2xl mx-auto mb-10">
                We continuously conduct HPLC testing on all of our raw powders as well as finished products to ensure the highest quality. You can have any product purchased from us tested at any HPLC licensed testing facility — and if the results are negative, we will refund the following:
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
                {/* HPLC Test Fee */}
                <div className="text-center">
                  <div className="relative inline-block">
                    <div className="absolute -inset-4 bg-[#00F0FF]/5 rounded-xl blur-lg" />
                    <div className="relative bg-[#111621] border border-[#1E2532] rounded-xl px-8 py-6">
                      <p className="text-[#00F0FF] text-xs font-bold uppercase tracking-[0.15em] mb-2">HPLC Test Fee</p>
                      <p className="text-5xl sm:text-6xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>$100</p>
                    </div>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="text-3xl font-bold text-[#00F0FF]">+</div>

                {/* Order Total */}
                <div className="text-center">
                  <div className="relative inline-block">
                    <div className="absolute -inset-4 bg-[#00F0FF]/5 rounded-xl blur-lg" />
                    <div className="relative bg-[#111621] border border-[#1E2532] rounded-xl px-8 py-6">
                      <p className="text-[#00F0FF] text-xs font-bold uppercase tracking-[0.15em] mb-2">Full Refund</p>
                      <p className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Order Total</p>
                      <p className="text-[#A1A7B5] text-xs mt-1">+ Shipping Fee</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-[#A1A7B5] text-xs mt-8">
                That's how confident we are in our products. No questions asked.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[#050505]/60" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Advance Your Research?
          </h2>
          <p className="text-[#A1A7B5] mb-8 max-w-2xl mx-auto">
            Join thousands of researchers who trust RENEW LABS for their peptide needs. 
            Experience the difference that premium quality makes.
          </p>
          <Link to="/products">
            <Button className="btn-primary animate-pulse-glow" data-testid="get-started-btn">
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
